import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DragHandle from '@tiptap/extension-drag-handle-react'
import { CircleDot, Copy, GripVertical, Plus, Trash2 } from 'lucide-react'
import { getEditorActions, insertEntityLink } from './editorActions'
import type { Editor } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { cn } from '@/lib/utils'

type Hovered = { node: PMNode; pos: number } | null

const TURN_INTO: Array<{
  id: string
  labelKey: string
  labelArgs?: Record<string, unknown>
  apply: (editor: Editor) => void
}> = [
  { id: 'text', labelKey: 'editor.toolbar.text', apply: (e) => e.chain().focus().setParagraph().run() },
  ...([1, 2, 3] as const).map((level) => ({
    id: `h${level}`,
    labelKey: 'editor.toolbar.heading',
    labelArgs: { level },
    apply: (e: Editor) => e.chain().focus().setNode('heading', { level }).run(),
  })),
  { id: 'bullet', labelKey: 'editor.toolbar.bulleted', apply: (e) => e.chain().focus().toggleBulletList().run() },
  { id: 'ordered', labelKey: 'editor.toolbar.numbered', apply: (e) => e.chain().focus().toggleOrderedList().run() },
  { id: 'todo', labelKey: 'editor.toolbar.todo', apply: (e) => e.chain().focus().toggleTaskList().run() },
  { id: 'toggle', labelKey: 'editor.toolbar.toggle', apply: (e) => e.chain().focus().setDetails().run() },
  { id: 'callout', labelKey: 'editor.toolbar.callout', apply: (e) => e.chain().focus().toggleCallout().run() },
  { id: 'quote', labelKey: 'editor.toolbar.quote', apply: (e) => e.chain().focus().toggleBlockquote().run() },
  { id: 'code', labelKey: 'editor.toolbar.code', apply: (e) => e.chain().focus().toggleCodeBlock().run() },
]

/**
 * Hovering a block shows + (insert below) and ⋮⋮ (drag, or click for the
 * block menu) in the margin, never inside the text (design 7a / 7c).
 */
export function BlockHandle({ editor }: { editor: Editor }) {
  const { t } = useTranslation('common')
  const hovered = useRef<Hovered>(null)
  const [menuFor, setMenuFor] = useState<Hovered>(null)

  const onNodeChange = useCallback(({ node, pos }: { node: PMNode | null; pos: number }) => {
    hovered.current = node ? { node, pos } : null
  }, [])

  const insertBelow = () => {
    const target = hovered.current
    if (!target) return
    const at = target.pos + target.node.nodeSize
    editor
      .chain()
      .insertContentAt(at, { type: 'paragraph', content: [{ type: 'text', text: '/' }] })
      .focus(at + 2)
      .run()
  }

  const selectBlock = (target: NonNullable<Hovered>) => {
    // Put the cursor inside the block so block commands act on it.
    editor.chain().focus().setTextSelection(Math.min(target.pos + 1, editor.state.doc.content.size)).run()
  }

  const canConvertToTask =
    !!getEditorActions(editor).createTask &&
    editor.extensionManager.extensions.some((e) => e.name === 'entityLink')

  const menuItem = 'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-secondary'

  return (
    <>
      <DragHandle editor={editor} onNodeChange={onNodeChange}>
        <div className="flex items-center gap-0.5 pr-1.5 text-muted-foreground">
          <button
            type="button"
            aria-label={t('editor.addBlock')}
            title={t('editor.addBlock')}
            onClick={insertBelow}
            className="flex size-6 items-center justify-center rounded-md hover:bg-secondary hover:text-foreground"
          >
            <Plus className="size-4" />
          </button>
          <button
            type="button"
            aria-label={t('editor.dragHandle')}
            title={t('editor.dragHandle')}
            onClick={() => hovered.current && setMenuFor(hovered.current)}
            className="flex h-6 w-5 cursor-grab items-center justify-center rounded-md hover:bg-secondary hover:text-foreground active:cursor-grabbing"
          >
            <GripVertical className="size-4" />
          </button>
        </div>
      </DragHandle>

      <Popover open={!!menuFor} onOpenChange={(open) => !open && setMenuFor(null)}>
        <PopoverAnchor asChild>
          <span
            aria-hidden
            className="pointer-events-none fixed"
            style={(() => {
              if (!menuFor) return { left: 0, top: 0 }
              const dom = editor.view.nodeDOM(menuFor.pos)
              const rect = dom instanceof HTMLElement ? dom.getBoundingClientRect() : null
              return { left: (rect?.left ?? 0) - 28, top: rect?.top ?? 0 }
            })()}
          />
        </PopoverAnchor>
        <PopoverContent align="start" side="left" className="w-52 p-1">
          {menuFor && (
            <div role="menu">
              <p className="px-2 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {t('editor.toolbar.turnInto')}
              </p>
              {TURN_INTO.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="menuitem"
                  className={menuItem}
                  onClick={() => {
                    selectBlock(menuFor)
                    option.apply(editor)
                    setMenuFor(null)
                  }}
                >
                  {t(option.labelKey, option.labelArgs)}
                </button>
              ))}
              <div className="my-1 border-t" />
              {canConvertToTask && menuFor.node.isTextblock && menuFor.node.textContent.trim() && (
                <button
                  type="button"
                  role="menuitem"
                  className={cn(menuItem, 'text-brand')}
                  onClick={() => {
                    const target = menuFor
                    setMenuFor(null)
                    const { createTask } = getEditorActions(editor)
                    void createTask?.(target.node.textContent.trim()).then((created) => {
                      if (!created) return
                      insertEntityLink(editor, created, ENTITY_TYPES.WORK_ITEM, {
                        from: target.pos + 1,
                        to: target.pos + target.node.nodeSize - 1,
                      })
                    })
                  }}
                >
                  <CircleDot className="size-3.5" />
                  {t('editor.toolbar.createTask')}
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className={menuItem}
                onClick={() => {
                  editor.chain().insertContentAt(menuFor.pos + menuFor.node.nodeSize, menuFor.node.toJSON()).run()
                  setMenuFor(null)
                }}
              >
                <Copy className="size-3.5" />
                {t('actions.duplicate', { defaultValue: 'Duplicate' })}
              </button>
              <button
                type="button"
                role="menuitem"
                className={cn(menuItem, 'text-destructive')}
                onClick={() => {
                  editor.chain().deleteRange({ from: menuFor.pos, to: menuFor.pos + menuFor.node.nodeSize }).run()
                  setMenuFor(null)
                }}
              >
                <Trash2 className="size-3.5" />
                {t('actions.delete', { defaultValue: 'Delete' })}
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  )
}
